import { getIndustrySlugForTeaor25 } from "./teaor25Mapping.js";

/** Search-flow `industry` values differ from TEÁOR slugs only for IT. */
const TEAOR_SLUG_TO_INDUSTRY_VALUE = {
  it: "IT",
};

/**
 * Convert a TEÁOR industry slug to the search-flow `company_store.industry` value.
 */
export const teaorSlugToIndustryValue = (slug) => {
  if (!slug || typeof slug !== "string") return undefined;
  return TEAOR_SLUG_TO_INDUSTRY_VALUE[slug] ?? slug;
};

const TEAOR_CODE_FIELD_NAMES = [
  "teaor_num",
  "teaor",
  "code",
  "teaor_code",
  "main_teaor",
];

const pickTeaorCodeFromValue = (value) => {
  if (value === undefined || value === null || value === "") return undefined;

  if (typeof value === "string" || typeof value === "number") {
    return parseTeaorFromActivityString(String(value));
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    for (const key of TEAOR_CODE_FIELD_NAMES) {
      const nested = value[key];
      if (nested !== undefined && nested !== null && nested !== "") {
        const parsed = pickTeaorCodeFromValue(nested);
        if (parsed) return parsed;
      }
    }
  }

  return undefined;
};

/**
 * Parse a Cégjelző activity string or raw code.
 * Examples:
 *   "63.11 Adatfeldolgozás, web-hoszting szolgáltatás"
 *   "6311 '08 Adatfeldolgozás, web-hoszting szolgáltatás"
 *   "62.01"
 */
export const parseTeaorFromActivityString = (raw) => {
  if (!raw || typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const dottedMatch = trimmed.match(/^(\d+(?:\.\d+)+)/);
  if (dottedMatch) return dottedMatch[1];

  const legacyMatch = trimmed.match(/^(\d{2,6})/);
  if (legacyMatch) return legacyMatch[1];

  return undefined;
};

const pickFirstTeaorCode = (values) => {
  for (const value of values) {
    const code = pickTeaorCodeFromValue(value);
    if (code) return code;
  }
  return undefined;
};

/**
 * Cégjelző `teaor_num` is often a flat digit string (e.g. `621002` → `62.10.02`).
 */
export const normalizeFlatCegjelzoTeaorNum = (code) => {
  if (code === undefined || code === null) return undefined;
  const raw = String(code).trim();
  if (!raw) return undefined;
  if (raw.includes(".")) return raw;

  const digits = raw.replace(/\D/g, "");
  if (!digits) return undefined;
  if (digits.length === 6) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 6)}`;
  }
  if (digits.length === 4) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}`;
  }
  return digits;
};

const sortActivitiesMainFirst = (activities) =>
  [...activities].sort((a, b) => {
    const aMain = a?.is_main_activity === true ? 0 : 1;
    const bMain = b?.is_main_activity === true ? 0 : 1;
    return aMain - bMain;
  });

/**
 * Extract the primary TEÁOR code from a Cégjelző company payload.
 */
export const extractPrimaryTeaorCodeFromCegjelzoCompany = (companyData) => {
  if (!companyData || typeof companyData !== "object") return undefined;

  const mainActivities = companyData.main_activities;
  if (Array.isArray(mainActivities) && mainActivities.length > 0) {
    const fromActivities = pickFirstTeaorCode(sortActivitiesMainFirst(mainActivities));
    if (fromActivities) return fromActivities;
  }

  const directFields = [
    companyData.main_activity,
    companyData.main_teaor,
    companyData.teaor,
    companyData.teaor_code,
  ];
  const fromDirect = pickFirstTeaorCode(directFields);
  if (fromDirect) return fromDirect;

  const nestedInfos = companyData.cegjelzoInfos ?? companyData.details?.cegjelzoInfos;
  if (nestedInfos?.main_activities && Array.isArray(nestedInfos.main_activities)) {
    const fromNested = pickFirstTeaorCode(
      nestedInfos.main_activities.map((activity) => activity?.teaor_num ?? activity),
    );
    if (fromNested) return fromNested;
  }

  return undefined;
};

/**
 * Map Cégjelző company data to a search-flow industry value, if TEÁOR is available.
 */
export const mapCegjelzoCompanyToIndustry = (companyData) => {
  const rawTeaorCode = extractPrimaryTeaorCodeFromCegjelzoCompany(companyData);
  if (!rawTeaorCode) return undefined;

  const teaorCode = normalizeFlatCegjelzoTeaorNum(rawTeaorCode) ?? rawTeaorCode;
  const slug = getIndustrySlugForTeaor25(teaorCode);
  if (!slug) return undefined;

  return teaorSlugToIndustryValue(slug);
};
